<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useDebounce } from '@/composables/useDebounce.js'
import { useRouter } from 'vue-router'
import { getRecord, listRecords } from '@/lib/records.js'
import { saveFilling } from '@/lib/saveFilling.js'
import { offline } from '@/lib/offline.js'
import { ENTITIES } from '@/lib/entities.js'
import { useSession } from '@/stores/session.js'
import { useUi } from '@/stores/ui.js'
import { useI18n } from 'vue-i18n'
import { useSave } from '@/composables/useSave.js'
import RefPicker from '@/components/form/RefPicker.vue'
import CellValue from '@/components/list/CellValue.vue'
import UiButton from '@/components/ui/UiButton.vue'
import RelatedActions from './RelatedActions.vue'

/**
 * Records related to this one (an account's opportunities, a contact's leads…), with "new"
 * pre-filled. With `link`, records made elsewhere are attached too (and detached): a lead
 * created in a hurry is put under its campaign later.
 */
const props = defineProps({
  entity: { type: String, required: true },
  title: { type: String, required: true },
  filter: { type: Object, required: true },
  columns: { type: Array, required: true },
  sort: { type: String, default: '' },
  defaults: { type: Object, default: null },
  // { field: 'campaign_uuid', ref: 'campaign', to: { uuid, label } } — the field of the listed records that points here.
  link: { type: Object, default: null },
  // An account in a capital group: the section can show the whole group's records (D-200).
  group: { type: String, default: null },
})
const router = useRouter()
const session = useSession()
const ui = useUi()
const rows = ref([])
const total = ref(0)
const loading = ref(false)
const { t } = useI18n()
const { report } = useSave()
const linking = ref(false)
const pick = ref(null)
const busy = ref(false)

const wholeGroup = ref(false)
// The whole group's records say whose they are.
const shownColumns = computed(() => (wholeGroup.value && !props.columns.includes('account') ? ['account', ...props.columns] : props.columns))
const filterNow = () => (props.group && wholeGroup.value ? { account_group: props.group } : props.filter)
async function load() {
  loading.value = true
  try {
    const res = await listRecords(props.entity, { filter: filterNow(), sort: props.sort || undefined, page: { size: 10 } }, { me: session.me })
    rows.value = res.data
    total.value = res.meta?.total ?? res.data.length
  } catch {
    rows.value = []
  } finally {
    loading.value = false
  }
}
watch(() => props.filter, load, { immediate: true, deep: true })
watch(wholeGroup, load)
// A pull brings changes one by one, hundreds at a time: one reload for them all, not one each.
const reloadOnFeed = useDebounce(() => load(), 400)
const stop = offline.on((event, payload) => {
  if ((event === 'change' || event === 'saved') && (payload?.entity === props.entity || payload?.operation?.entity === props.entity)) reloadOnFeed()
})
onBeforeUnmount(() => stop())

function create() {
  // In a panel beside the record (an offer is built in its own editor, on its own page).
  if (props.entity !== 'offer') {
    ui.openCreate({ entity: props.entity, defaults: props.defaults || {}, onSaved: () => load() })
  } else {
    router.push({ name: 'record-new', params: { entityPath: ENTITIES[props.entity].path }, query: Object.fromEntries(Object.entries(props.defaults || {}).filter(([, v]) => typeof v === 'string')) })
  }
}
function openRow(r, e) {
  // A related record opens in a panel from the right; with Ctrl/⌘ or Shift, its page in a new tab.
  const to = props.entity === 'offer' ? { name: 'offer-editor', params: { uuid: r.uuid } } : { name: 'record', params: { entityPath: ENTITIES[props.entity].path, uuid: r.uuid } }
  if (e && (e.ctrlKey || e.metaKey || e.shiftKey)) window.open(router.resolve(to).href, '_blank', 'noopener')
  else if (props.entity === 'offer') router.push(to)
  else ui.openRecord({ entity: props.entity, uuid: r.uuid })
}
function showAll() {
  router.push({ name: 'list', params: { entityPath: ENTITIES[props.entity].path }, query: { f: JSON.stringify(filterNow()) } })
}
async function attach() {
  if (!pick.value) return
  busy.value = true
  try {
    const target = await getRecord(props.entity, pick.value).catch(() => null)
    const current = target?.[props.link.ref]
    // Already under another one: moved only when the person says so.
    if (current?.uuid && current.uuid !== props.link.to.uuid
      && !(await ui.ask({ title: t('related.moveTitle'), message: t('related.moveMessage', { record: target.label, from: current.label, to: props.link.to.label }), confirmLabel: t('related.move') }))) return
    const result = report(await saveFilling(props.entity, pick.value, { [props.link.field]: props.link.to.uuid }, { baseVersion: target?.version ?? null, label: target?.label }), { success: t('related.attached') })
    if (['saved', 'queued'].includes(result.status)) {
      pick.value = null
      linking.value = false
      load()
    }
  } finally {
    busy.value = false
  }
}
async function detach(r) {
  if (!(await ui.ask({ title: t('related.detachTitle'), message: t('related.detachMessage', { record: r.label, from: props.link.to.label }), confirmLabel: t('related.detach') }))) return
  const result = report(await saveFilling(props.entity, r.uuid, { [props.link.field]: null }, { baseVersion: r.version ?? null, label: r.label }), { success: t('related.detached') })
  if (['saved', 'queued'].includes(result.status)) load()
}
const canLink = () => props.link && (session.has(`${props.entity}:update`) || session.has(`${props.entity}:own_update`))
defineExpose({ reload: load })
</script>

<template>
  <section class="card">
    <header class="flex items-center gap-2 border-b border-default px-3 py-2">
      <i :class="['fa-solid text-sm', ENTITIES[entity].icon, ENTITIES[entity].color]" aria-hidden="true"></i>
      <h3 class="flex-1 text-sm font-semibold">{{ title }} <span class="font-normal text-faint">({{ total }})</span></h3>
      <div v-if="group" class="flex rounded-lg border border-default p-0.5 text-xs" role="radiogroup" :aria-label="$t('relationships.scopeLabel')">
        <button v-for="s in [false, true]" :key="String(s)" type="button" role="radio" :aria-checked="wholeGroup === s" :data-testid="`related-${entity}-${s ? 'group' : 'account'}`"
          :class="['rounded-md px-2 py-0.5', wholeGroup === s ? 'bg-brand-600 text-white' : 'text-muted hover:bg-[var(--surface-3)]']" @click="wholeGroup = s">{{ $t(`relationships.scope.${s ? 'group' : 'account'}`) }}</button>
      </div>
      <RelatedActions :can-create="!!defaults && session.has(`${entity}:create`)" :can-link="!!canLink()" :linking="linking" :testid="`related-${entity}`" @create="create" @link="linking = !linking" />
    </header>
    <div v-if="linking" class="flex flex-wrap gap-2 border-b border-default p-3">
      <RefPicker v-model="pick" :entity="entity" :allow-create="false" class="min-w-48 flex-1" :placeholder="$t('related.findExisting')" :data-testid="`related-pick-${entity}`" />
      <UiButton variant="primary" icon="fa-link" :disabled="!pick" :loading="busy" :data-testid="`related-attach-${entity}`" @click="attach">{{ $t('related.attach') }}</UiButton>
    </div>
    <p v-if="loading && !rows.length" class="px-3 py-3 text-sm text-faint"><i class="fa-solid fa-circle-notch fa-spin" aria-hidden="true"></i></p>
    <p v-else-if="!rows.length" class="px-3 py-3 text-sm text-faint">{{ $t('common.empty') }}</p>
    <ul v-else class="divide-y divide-[var(--border)]">
      <li v-for="r in rows" :key="r.uuid" class="group flex cursor-pointer items-center gap-2 px-3 py-2 hover:bg-[var(--surface-3)]" :data-row="r.label" @click="(e) => openRow(r, e)">
        <div class="min-w-0 flex-1">
          <p class="truncate text-sm font-medium">{{ r.label }}</p>
          <div class="flex flex-wrap gap-x-3 text-xs text-muted">
            <span v-for="c in shownColumns" :key="c"><CellValue :entity="entity" :column="c" :record="r" /></span>
          </div>
        </div>
        <UiButton v-if="canLink()" size="xs" variant="ghost" icon="fa-link-slash" class="shrink-0 opacity-60 hover:text-red-600 group-hover:opacity-100" :title="$t('related.detach')" :aria-label="$t('related.detach')" @click.stop="detach(r)" />
      </li>
    </ul>
    <button v-if="total > rows.length" type="button" class="link w-full border-t border-default px-3 py-2 text-left text-xs" @click="showAll">{{ $t('search.showAll', { n: total }) }}</button>
  </section>
</template>
